import React, { useState, useEffect, useRef } from 'react';
import { TeamDto, fetchTeamsNotInHeat } from '../api/apis';

interface TeamSelectorProps {
  selectedTeams: TeamDto[];
  onTeamSelect: (team: TeamDto) => void;
  onTeamRemove: (teamId: string) => void;
}

export const TeamSelector: React.FC<TeamSelectorProps> = ({
  selectedTeams,
  onTeamSelect,
  onTeamRemove,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [availableTeams, setAvailableTeams] = useState<TeamDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load all teams initially and when search query changes
  useEffect(() => {
    const loadTeams = async () => {
      try {
        setIsLoading(true);
        const teams = await fetchTeamsNotInHeat(searchQuery);
        // Filter out teams that are already selected
        const filteredTeams = teams.filter(
          team => !selectedTeams.some(selected => selected.id === team.id)
        );
        setAvailableTeams(filteredTeams);
      } catch (error) {
        console.error('Error loading teams:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(loadTeams, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, selectedTeams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (team: TeamDto) => {
    onTeamSelect(team);
    setSearchQuery('');
    setIsOpen(false);
  };

  return (
    <div className="space-y-4">
      {/* Selected Teams */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">Selected Teams</h3>
        <div className="space-y-2">
          {selectedTeams.map((team) => (
            <div
              key={team.id}
              className="flex justify-between items-center p-2 bg-gray-50 rounded border border-gray-200"
            >
              <span className="font-medium">{team.name}</span>
              <button
                onClick={() => onTeamRemove(team.id)}
                className="text-red-500 hover:text-red-600"
              >
                Remove
              </button>
            </div>
          ))}
          {selectedTeams.length === 0 && (
            <p className="text-gray-500 text-sm">No teams selected</p>
          )}
        </div>
      </div>

      {/* Team Dropdown */}
      <div ref={dropdownRef} className="relative">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Add Teams</h3>
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder="Search teams..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {isLoading && (
            <div className="absolute right-3 top-2.5">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {availableTeams.length > 0 ? (
              availableTeams.map((team) => (
                <button
                  key={team.id}
                  onClick={() => handleSelect(team)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
                >
                  {team.name}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">
                {isLoading ? 'Loading...' : 'No teams without heat found'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}; 